import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import ContainerPage from "../../components/Containers/ContainerPage";
import TitlePage from "../../components/Texts/TitlePage";
import TextField from "../../components/Form/TextField";
import Button from "../../components/Buttons/Button";
import CardContainer from "../../components/Card/CardContainer";
import { SimpleAlert } from "../../components/Alerts/Alerts";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startPublishGlobalNotification } from "../../actions/owner";
import {
  isAdleAppGlobalNotificationLink,
  isValidGlobalNotificationLink,
} from "../../utils/globalNotifications";
import { APP_SUPPORTED_LANGUAGES } from "../../utils/i18nLanguage";
import { ALERT_ICON_TYPE_SUCCESS } from "../../utils/constants";
import {
  ENGLISH,
  ERROR_OCCURRED,
  FIELD_REQUIRED,
  FIELD_URL_ADLE_APP_DOMAIN,
  FIELD_URL_VALID,
  OWNER_DASHBOARD_NAV_GLOBAL_NOTIFICATIONS,
  OWNER_GLOBAL_NOTIFICATIONS_FIELD_BODY,
  OWNER_GLOBAL_NOTIFICATIONS_FIELD_CTA,
  OWNER_GLOBAL_NOTIFICATIONS_FIELD_LINK,
  OWNER_GLOBAL_NOTIFICATIONS_FIELD_TITLE,
  OWNER_GLOBAL_NOTIFICATIONS_PUBLISH_BUTTON,
  OWNER_GLOBAL_NOTIFICATIONS_PUBLISH_SUCCESS,
  OWNER_GLOBAL_NOTIFICATIONS_TITLE,
  PORTUGUESE,
  SPANISH,
} from "../../i18n/keysTranslations";

const LANGUAGE_LABEL_KEYS = {
  en: ENGLISH,
  es: SPANISH,
  pt: PORTUGUESE,
};

const emptyLocale = () => ({
  title: "",
  body: "",
  cta: "",
});

const buildDefaultValues = () => ({
  i18n: APP_SUPPORTED_LANGUAGES.reduce((locales, code) => {
    locales[code] = emptyLocale();
    return locales;
  }, {}),
  link: "",
});

const createGlobalNotificationSchema = (t) =>
  z.object({
    i18n: z.object(
      APP_SUPPORTED_LANGUAGES.reduce((shape, code) => {
        shape[code] = z.object({
          title: z.string().trim().min(1, t(FIELD_REQUIRED)),
          body: z.string().trim().min(1, t(FIELD_REQUIRED)),
          cta: z.string().trim().min(1, t(FIELD_REQUIRED)),
        });
        return shape;
      }, {})
    ),
    link: z
      .string()
      .trim()
      .min(1, t(FIELD_REQUIRED))
      .refine((value) => isValidGlobalNotificationLink(value), {
        message: t(FIELD_URL_VALID),
      })
      .refine(
        (value) =>
          !isValidGlobalNotificationLink(value) ||
          isAdleAppGlobalNotificationLink(value),
        { message: t(FIELD_URL_ADLE_APP_DOMAIN) }
      ),
  });

const LanguageCardTitle = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      {children}
    </Typography>
  </Box>
);

const OwnerGlobalNotificationsBrief = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { theme } = useSelectorApp((state) => state.ui);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const schema = useMemo(() => createGlobalNotificationSchema(t), [t]);
  const defaultValues = useMemo(() => buildDefaultValues(), []);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: zodResolver(schema),
    mode: "onSubmit",
  });

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const result = await dispatch(
        startPublishGlobalNotification({
          i18n: values.i18n,
          link: values.link,
        })
      );

      if (!result?.ok) {
        await SimpleAlert({
          title: t(ERROR_OCCURRED),
          icon: "error",
        });
        return;
      }

      await SimpleAlert({
        title: t(OWNER_GLOBAL_NOTIFICATIONS_PUBLISH_SUCCESS),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      reset(buildDefaultValues());
    } catch {
      await SimpleAlert({
        title: t(ERROR_OCCURRED),
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ContainerPage hasMarginBottom={false}>
      <Helmet>
        <title>
          {`${theme.nameBusiness} - ${t(OWNER_DASHBOARD_NAV_GLOBAL_NOTIFICATIONS)}`}
        </title>
      </Helmet>
      <Box sx={{ width: "100%", px: 3, mt: 4, flexShrink: 0 }}>
        <TitlePage sx={{ mb: 3 }}>
          {t(OWNER_GLOBAL_NOTIFICATIONS_TITLE)}
        </TitlePage>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          width: "100%",
          px: 3,
          mb: 4,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gap: 3,
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(auto-fit, minmax(280px, 1fr))",
            },
            alignItems: "stretch",
          }}
        >
          {APP_SUPPORTED_LANGUAGES.map((code) => (
            <CardContainer
              key={code}
              dynamicHeight
              title={
                <LanguageCardTitle>
                  {t(LANGUAGE_LABEL_KEYS[code] || code)}
                </LanguageCardTitle>
              }
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  label={t(OWNER_GLOBAL_NOTIFICATIONS_FIELD_TITLE)}
                  name={`i18n.${code}.title`}
                  variant="filled"
                  color="primary"
                  InputLabelProps={{ shrink: true }}
                  control={control}
                  errors={errors}
                />
                <TextField
                  fullWidth
                  label={t(OWNER_GLOBAL_NOTIFICATIONS_FIELD_BODY)}
                  name={`i18n.${code}.body`}
                  variant="filled"
                  color="primary"
                  multiline
                  minRows={4}
                  InputLabelProps={{ shrink: true }}
                  control={control}
                  errors={errors}
                />
                <TextField
                  fullWidth
                  label={t(OWNER_GLOBAL_NOTIFICATIONS_FIELD_CTA)}
                  name={`i18n.${code}.cta`}
                  variant="filled"
                  color="primary"
                  InputLabelProps={{ shrink: true }}
                  control={control}
                  errors={errors}
                />
              </Box>
            </CardContainer>
          ))}
        </Box>

        <CardContainer
          dynamicHeight
          title={
            <LanguageCardTitle>
              {t(OWNER_GLOBAL_NOTIFICATIONS_FIELD_LINK)}
            </LanguageCardTitle>
          }
        >
          <TextField
            fullWidth
            name="link"
            variant="filled"
            color="primary"
            InputLabelProps={{ shrink: true }}
            control={control}
            errors={errors}
          />
        </CardContainer>

        <Box>
          <Button type="submit" color="primary" loading={isSubmitting}>
            {t(OWNER_GLOBAL_NOTIFICATIONS_PUBLISH_BUTTON)}
          </Button>
        </Box>
      </Box>
    </ContainerPage>
  );
};

export default OwnerGlobalNotificationsBrief;
